<template>
  <base-page-layout title="Resultados gerais">
    <v-row>
      <v-col>
        <new-filters-menu
          class="mb-4"
          show-outdated-warning
          :filters="['seletor-mes', 'industria-rebate', 'industrias', 'industria-publico-alvo', 'industria-mecanica']"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="3">
        <cbm-card
          title="Reembolso"
          is-currency
          :number-text="stateFilters.month ? localeString(rebateSum, 2) : '0'"
          number-color="#0A8533"
          simple-card
          :percent-description="stateFilters.month ? 'Valor total gerado pelas ofertas' : ''"
          tooltip="Reembolso total das ofertas dentro dos critérios das filtragens, considerando o reembolso em processamento e o reembolso completo."
          :boolean-loader="$apollo.queries.bigNumbers.loading"
        >
          <template #additional>
            <div v-if="!stateFilters.month || stateFilters.month === ''">
              <span :style="{ color: 'red' }">Selecione um mês para exibir essa informação</span>
            </div>
            <div v-else class="extra-rebate-container">
              <div class="d-flex align-center justify-space-between" style="gap: 6px">
                <div>
                  <span class="rebate-counting-value"> {{ locale(rebateCounting, 2) | localeString(2) | currency }} </span>
                  <span class="rebate-counting-text">Em apuração</span>
                </div>
                <tooltip message="Valor gerado nas ofertas. Ainda pode sofrer alterações." align="top" variant="secondary">
                  <mf-icon small>info</mf-icon>
                </tooltip>
              </div>

              <div class="d-flex align-center space-between justify-space-between" style="gap: 6px">
                <div>
                  <span class="rebate-counted-value"> {{ locale(rebateCounted, 2) | localeString(2) | currency }} </span>
                  <span class="rebate-counted-text">Apuração concluída</span>
                </div>
                <tooltip message="Valor de ofertas que já concluíram a apuração e já estão com o valor final." align="top" variant="secondary">
                  <mf-icon small>info</mf-icon>
                </tooltip>
              </div>
            </div>
          </template>
        </cbm-card>
      </v-col>

      <v-col cols="3">
        <cbm-card
          title="Faturamento"
          is-currency
          :number-text="stateFilters.month ? localeString(bigNumbers.revenue, 2) : '0'"
          tooltip="Total de faturamento das ofertas dentro da filtragem selecionada."
          simple-card
          :boolean-loader="$apollo.queries.bigNumbers.loading"
        >
          <template #additional>
            <div v-if="!stateFilters.month || stateFilters.month === ''">
              <span :style="{ color: 'red' }">Selecione um mês para exibir essa informação</span>
            </div>
          </template>
        </cbm-card>
      </v-col>

      <v-col cols="3">
        <cbm-card
          title="Produtos que geraram benefício"
          :number-text="stateFilters.month ? localeString(bigNumbers.sale_units, 0) : '0'"
          tooltip="Total de produtos vendidos em que as ofertas foram aplicadas. Considera apenas os produtos que geraram e/ou receberam o desconto."
          simple-card
          :boolean-loader="$apollo.queries.bigNumbers.loading"
        >
          <template #additional>
            <div v-if="!stateFilters.month || stateFilters.month === ''">
              <span :style="{ color: 'red' }">Selecione um mês para exibir essa informação</span>
            </div>
          </template>
        </cbm-card>
      </v-col>

      <v-col cols="3">
        <cbm-card
          title="Ticket médio"
          is-currency
          :number-text="stateFilters.month ? localeString(bigNumbers.average_ticket, 2) : '0'"
          simple-card
          :boolean-loader="$apollo.queries.bigNumbers.loading"
        >
          <template #additional>
            <div v-if="!stateFilters.month || stateFilters.month === ''">
              <span :style="{ color: 'red' }">Selecione um mês para exibir essa informação</span>
            </div>
          </template>
        </cbm-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <general-results-table title="Resultados por oferta" :query-variables="queryVariables" :franchise="false" />
      </v-col>
    </v-row>

    <v-row v-if="isFranchisee">
      <v-col>
        <general-results-table title="Resultados por oferta da rede" :query-variables="queryVariables" :franchise="true" />
      </v-col>
    </v-row>
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import Tooltip from '@/modules/campanhas/gestao-ofertas-v2/views/components/Tooltip.vue'
import { localeString } from '@/config/helpers.js'
import { QUERY_BIG_NUMBERS_GENERAL_RESULTS } from '../../graphql'
import GeneralResultsTable from '../components/GeneralResultsTable.vue'
import CbmCard from '@/components/CbmCard.vue'
import { mapState, mapGetters } from 'vuex'

export default {
  components: {
    BasePageLayout,
    Tooltip,
    GeneralResultsTable,
    CbmCard,
    NewFiltersMenu: () => import('@/components/filters-menu/NewFiltersMenu.vue'),
  },
  data: () => ({
    loading: false,
    locale: localeString,
    bigNumbers: {
      revenue: 0,
      rebate: {
        counting: 0,
        counted: 0,
      },
      sale_units: 0,
      average_ticket: 0,
    },
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),

    ...mapGetters(['getSelectedIndustriesIds']),

    queryVariables() {
      let queryVariables = {
        startDate: this.stateFilters.month,
        endDate: this.stateFilters.month,
        target: this.stateFilters.segmentType || [],
        format: this.stateFilters.mechanics || [],
        status: this.stateFilters.rebateStatus || [],
        industryIds: this.getSelectedIndustriesIds,
      }

      if (this.stateFilters.mechanics.includes('nextbuycashback')) {
        queryVariables.format = this.stateFilters.mechanics.concat('cashback_absolute')
      }

      return queryVariables
    },

    isFranchisee() {
      if (!this.$getLoggedUser().is_franchisee) return false
      return this.$getLoggedUser().is_franchisee
    },

    rebateCounting() {
      return this.bigNumbers?.rebate?.counting || 0
    },

    rebateCounted() {
      return this.bigNumbers?.rebate?.counted || 0
    },

    rebateSum() {
      return this.rebateCounting + this.rebateCounted
    },
  },
  apollo: {
    bigNumbers: {
      query: QUERY_BIG_NUMBERS_GENERAL_RESULTS,
      fetchPolicy: 'network-only',
      context() {
        return {
          uri: this.$microservicesUrls['sales'],
        }
      },
      variables() {
        let variables = {
          franchise: this.isFranchisee,
          startDate: this.stateFilters.month,
          endDate: this.stateFilters.month,
          target: this.stateFilters.segmentType || [],
          format: this.stateFilters.mechanics || [],
          status: this.stateFilters.rebateStatus || [],
          industryIds: this.getSelectedIndustriesIds,
        }

        if (this.stateFilters.mechanics.includes('nextbuycashback')) {
          variables.format = this.stateFilters.mechanics.concat('cashback_absolute')
        }

        return variables
      },
      update(result) {
        return result.generalResultsIndustry
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.extra-rebate-container {
  margin-top: 12px;
}

.rebate-counting-value {
  font-size: 18px;
  color: $marca-2-d20;
  font-weight: 700;
}

.rebate-counting-text {
  color: $marca-2-d20;
  width: 122px;
}

.rebate-counted-value {
  font-size: 18px;
  color: #0a8533;
  font-weight: 700;
}

.rebate-counted-text {
  width: 122px;
  color: #0a8533;
}
</style>
